import React from "react";
import { Tooltip, IconButton } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IosShareIcon from "@mui/icons-material/IosShare";
import StopIcon from "@mui/icons-material/Stop";
import CheckIcon from "@mui/icons-material/Check";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PropTypes from "prop-types";


const buttonStyles = {
    start: {
        bgcolor: "#e0f7fa",
        "&:hover": { bgcolor: "#b2ebf2" },
        border: "1px solid #00acc1",
    },
    stop: {
        bgcolor: "#ffebee",
        "&:hover": { bgcolor: "#ffcdd2" },
        border: "1px solid #f44336",
    },
    delivery: {
        bgcolor: "#e8f5e9",
        "&:hover": { bgcolor: "#c8e6c9" },
        border: "1px solid #4caf50",
    },
    validate: {
        bgcolor: "#c6c9edff",
        "&:hover": { bgcolor: "#b8badeff" },
        border: "1px solid #5a48cdff",
    },
};

const commonButtonSx = {
    borderRadius: "50%",
    padding: "4px",
};

const ActionButton = ({ title, onClick, sx, children }) => (
    <Tooltip title={title}>
        <IconButton onClick={onClick} sx={{ ...sx, ...commonButtonSx }}>
            {children}
        </IconButton>
    </Tooltip>
);

ActionButton.propTypes = {
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    sx: PropTypes.object,
    children: PropTypes.node.isRequired,
};

export default function BatchActionButtons({
                                               id,
                                               batchStatus,
                                               daemonStatus,
                                               deliveryDaemonStatus,
                                               qualityCheckDaemonStatus,
                                               openConfirmation,
                                               openSelectOCRTypeConfirmation
                                           }) {
    const renderDigitizationButton = () =>
        daemonStatus === "stopped" ? (
            <ActionButton
                title="Start Digitization"
                onClick={() => openSelectOCRTypeConfirmation("batch", "start", id)}
                sx={buttonStyles.start}
            >
                <PlayArrowIcon />
            </ActionButton>
        ) : (
            <ActionButton
                title="Stop Digitization"
                onClick={() => openConfirmation("batch", "stop", id)}
                sx={buttonStyles.stop}
            >
                <PauseIcon />
            </ActionButton>
        );

    const renderDeliveryButton = () =>
        deliveryDaemonStatus === "stopped" ? (
            <ActionButton
                title="Start Delivery"
                onClick={() =>
                    openConfirmation("batch", "startDelivery", id)
                }
                sx={buttonStyles.delivery}
            >
                <IosShareIcon />
            </ActionButton>
        ) : (
            <ActionButton
                title="Stop Delivery"
                onClick={() =>
                    openConfirmation("batch", "stopDelivery", id)
                }
                sx={buttonStyles.stop}
            >
                <StopIcon />
            </ActionButton>
        );

    const renderQualityCheckButton = () => {
        const isStopped = qualityCheckDaemonStatus === "stopped";

        if (batchStatus === "in_progress" && isStopped) {
            return null;
        }

        return isStopped ? (
            <ActionButton
                title="Start Quality Check"
                onClick={() =>
                    openConfirmation("batch", "startQualityCheck", id)
                }
                sx={buttonStyles.start}
            >
                <MonitorHeartIcon />
            </ActionButton>
        ) : (
            <ActionButton
                title="Stop Quality Check"
                onClick={() =>
                    openConfirmation("batch", "stopQualityCheck", id)
                }
                sx={buttonStyles.stop}
            >
                <PauseIcon />
            </ActionButton>
        );
    };

    switch (batchStatus) {
        case "pending":
            return (
                <>
                    <ActionButton
                        title="Start Digitization"
                        onClick={() =>
                            openSelectOCRTypeConfirmation("batch", "start", id)
                        }
                        sx={buttonStyles.start}
                    >
                        <PlayArrowIcon />
                    </ActionButton>
                    {renderQualityCheckButton()}
                </>
            );

        case "partially_uploaded":
            return (
                <ActionButton
                    title="Upload Missing Files"
                    onClick={() =>
                        openConfirmation(
                            "batch",
                            "uploadMissingFiles",
                            id
                        )
                    }
                    sx={buttonStyles.start}
                >
                    <RestartAltIcon />
                </ActionButton>
            );

        case "partially_done":
        case "in_progress":
            return (
                <>
                    {renderDigitizationButton()}
                    {renderDeliveryButton()}
                    {renderQualityCheckButton()}
                </>
            );

        case "partially_delivered":
        case "done":
            return (
                <>
                    {renderDeliveryButton()}
                    {renderQualityCheckButton()}
                </>
            );

        case "delivered":
            return (
                <>
                    <ActionButton
                        title="Validate Batch"
                        onClick={() =>
                            openConfirmation("batch", "accept", id)
                        }
                        sx={buttonStyles.validate}
                    >
                        <CheckIcon />
                    </ActionButton>
                    {renderQualityCheckButton()}
                </>
            );

        default:
            return null;
    }
}

BatchActionButtons.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    batchStatus: PropTypes.string.isRequired,
    daemonStatus: PropTypes.string.isRequired,
    deliveryDaemonStatus: PropTypes.string.isRequired,
    qualityCheckDaemonStatus: PropTypes.string.isRequired,
    openConfirmation: PropTypes.func.isRequired,
    openSelectOCRTypeConfirmation:  PropTypes.func.isRequired
};